import React, { useState } from 'react';

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  prompt: string;
  color: string;
}

interface QuickActionsPanelProps {
  onSelectAction: (prompt: string) => void;
  disabled?: boolean;
}

const quickActions: QuickAction[] = [
  {
    id: 'requirements',
    label: '需求分析',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    prompt: '请帮我分析这个页面的需求，包括目标用户、核心功能、内容结构等方面。',
    color: 'blue',
  },
  {
    id: 'architecture',
    label: '架构设计',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    prompt: '请帮我设计这个页面的技术架构，包括组件结构、数据流、状态管理等方面。',
    color: 'purple',
  },
  {
    id: 'frontend',
    label: '前端开发',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    prompt: '请帮我生成前端代码，包括HTML结构、CSS样式和JavaScript交互逻辑。',
    color: 'green',
  },
  {
    id: 'backend',
    label: '后端开发',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 01-2 2v4a2 2 0 012 2h14a2 2 0 012-2v-4a2 2 0 01-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    prompt: '请帮我设计后端API接口，包括路由定义、数据模型、业务逻辑等方面。',
    color: 'orange',
  },
  {
    id: 'testing',
    label: '测试验收',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    prompt: '请帮我制定测试计划，包括功能测试、性能测试、验收标准等方面。',
    color: 'pink',
  },
];

const colorClasses: Record<string, { bg: string; hover: string; text: string; border: string }> = {
  blue: { bg: 'bg-blue-50', hover: 'hover:bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  purple: { bg: 'bg-purple-50', hover: 'hover:bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
  green: { bg: 'bg-green-50', hover: 'hover:bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  orange: { bg: 'bg-orange-50', hover: 'hover:bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
  pink: { bg: 'bg-pink-50', hover: 'hover:bg-pink-100', text: 'text-pink-600', border: 'border-pink-200' },
};

export const QuickActionsPanel: React.FC<QuickActionsPanelProps> = ({
  onSelectAction,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleActionClick = (action: QuickAction) => {
    if (disabled) return;
    onSelectAction(action.prompt);
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50/50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 flex items-center justify-between text-xs text-gray-500 hover:text-gray-700 transition-colors"
        disabled={disabled}
      >
        <span className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          快捷功能
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Quick Actions Grid */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-3 grid grid-cols-5 gap-2">
          {quickActions.map((action) => {
            const colors = colorClasses[action.color];
            return (
              <button
                key={action.id}
                onClick={() => handleActionClick(action)}
                disabled={disabled}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border ${colors.bg} ${colors.border} ${colors.hover} ${colors.text} transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group`}
                title={action.label}
              >
                <span className="group-hover:scale-110 transition-transform">
                  {action.icon}
                </span>
                <span className="text-[10px] font-medium whitespace-nowrap">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
